import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { authAPI } from '../../identity/api/auth';
import { useAuthStore } from '../../identity/store/authStore';
import { mailerOrdersAPI } from '../api/mailer-orders';

/**
 * Fetch all past mailer orders — works for both authenticated and anonymous sessions
 * (apiClient sends anon_session_token as fallback)
 */
export function useMailerOrders() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasAnonSession = Boolean(localStorage.getItem('anon_session_token'));

  return useQuery({
    queryKey: ['mailer-orders'],
    queryFn: mailerOrdersAPI.getOrders,
    enabled: isAuthenticated || hasAnonSession,
  });
}

/**
 * Get a pricing preview based on recipients and donation amount
 * Only fetches if payload is complete and valid
 */
export function useMailerPreview(payload) {
  return useQuery({
    queryKey: ['mailer-preview', payload],
    queryFn: () => mailerOrdersAPI.preview(payload),
    enabled: Boolean(payload?.recipients?.length > 0),
  });
}

/**
 * Submit a mailer order and redirect to Stripe Checkout.
 * For guests, creates an anonymous session first so the order can be attributed
 * to a temporary user record. The guest can upgrade to a full account post-payment.
 */
export function useCreateMailerOrder() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const mutation = useMutation({
    mutationFn: async (payload) => {
      if (!isAuthenticated && !localStorage.getItem('anon_session_token')) {
        const { accessToken } = await authAPI.createAnonymousSession();
        localStorage.setItem('anon_session_token', accessToken);
      }
      return mailerOrdersAPI.create(payload);
    },
    onSuccess: ({ data }) => {
      // Give the UI a beat to swap into "Redirecting to payment..." before
      // the browser navigates away — otherwise the click feels unresponsive.
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = data.checkoutUrl;
      }, 250);
    },
  });

  return {
    createOrder: mutation.mutate,
    isLoading: mutation.isPending,
    isRedirecting,
    error: mutation.error,
  };
}
